import {
  Button,
  LabeledDatePickerInput,
  LabeledInput,
  ModalFooterAction,
  useAlert,
  useToast,
} from "@apex/component-library";
import {
  Box,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
} from "@chakra-ui/react";
import { Form, Formik, FormikHelpers } from "formik";
import { withFormikDevtools } from "formik-devtools-extension";
import { useRef } from "react";
import { useQueryClient } from "react-query";

import queryKeys from "../../../api/queryKeys";
import { CallAlertData } from "../../../api/useGetCallOrAlert";
import { usePostModifyCall } from "../../../api/usePostModifyCall";
import { CallDetailResultFromAPI, GetCallsFromAPI } from "../../../types";
import { SUCCESS_TOAST } from "../../../utils/constants";
import {
  dateToIso,
  getDatePickerFormattedDate,
} from "../../../utils/dateFormatter";
import getDateString from "../../../utils/getDateString";
import { formatNumber } from "../../../utils/numberFormatter";
import InputFieldWrapper from "../../InputFieldWrapper";
import { FIELD_WIDTH } from "../../InputFieldWrapper/InputFieldWrapper";
import ModalInfoItem from "../../ModalInfoItem";
import modifyCallFormSchema from "./modifyCallFormValidation";

export interface ModifyCallFormValues {
  issueDate: string;
  dueDate: Date | string;
  callAmountDisplay: string;
  callAmountRaw?: number;
}

interface ModifyCallModalProps {
  isOpen: boolean;
  onClose: () => void;
  record: CallDetailResultFromAPI;
  onSuccess?: () => void;
}

const ModifyCallModal = ({
  isOpen,
  onClose,
  record,
  onSuccess,
}: ModifyCallModalProps) => {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const {
    accountHolderName,
    account,
    eventCode,
    tradeDate,
    issueDate,
    dueDate,
    callAmount,
    eventId,
  } = record;

  const toast = useToast();
  const { removeAllAlerts } = useAlert();
  const queryClient = useQueryClient();

  const { mutate, isLoading } = usePostModifyCall(eventId, {
    onSuccess: data => {
      toast({
        ...SUCCESS_TOAST,
        description: "The margin call has been updated.",
      });

      !!queryClient.getQueryState([queryKeys.getCallOrAlert, eventId]) &&
        queryClient.setQueryData<CallAlertData | undefined>(
          [queryKeys.getCallOrAlert, eventId],
          prev => {
            if (prev) {
              return {
                ...prev,
                callAmount: data.callAmount,
              };
            }
            return prev;
          }
        );

      !!queryClient.getQueryState(queryKeys.getMarginCalls) &&
        queryClient.setQueryData(
          [queryKeys.getMarginCalls],
          (prev: GetCallsFromAPI | undefined) => ({
            totalRows: prev!.totalRows,
            records: prev!.records.map(call => {
              if (call.eventId === eventId && call.account === account) {
                return {
                  ...call,
                  dueDate: dateToIso(data.dueDate),
                  callAmount: data.callAmount || 0,
                };
              }
              return call;
            }),
          })
        );
      !!onSuccess && onSuccess();
      removeAllAlerts();
    },
    onSettled: () => {
      onClose();
    },
  });

  const isFormUpdated = (values: ModifyCallFormValues) => {
    return (
      getDateString(dueDate) !== values.dueDate ||
      callAmount !== values.callAmountRaw
    );
  };

  const handleSubmit = (
    values: ModifyCallFormValues,
    formikHelpers: FormikHelpers<ModifyCallFormValues>
  ) => {
    mutate({ dueDate: values.dueDate, callAmount: values.callAmountRaw });
    formikHelpers.setSubmitting(false);
  };

  const formikConfig = {
    onSubmit: handleSubmit,
    initialValues: modifyCallFormSchema.cast({
      issueDate,
      dueDate: getDatePickerFormattedDate(getDateString(dueDate)),
      callAmountDisplay: formatNumber(callAmount, 2),
      callAmountRaw: callAmount,
    }) as ModifyCallFormValues,
    validationSchema: modifyCallFormSchema,
    validateOnMount: true,
  };

  return (
    <Modal
      motionPreset="slideInBottom"
      initialFocusRef={cancelRef}
      onClose={onClose}
      isOpen={isOpen}
      isCentered
    >
      <ModalOverlay />
      <Formik {...formikConfig}>
        {formikProps => {
          withFormikDevtools(formikProps);
          const {
            handleBlur,
            values,
            setValues,
            setFieldValue,
            touched,
            errors,
            isValid,
            resetForm,
          } = formikProps;
          return (
            <Form>
              <ModalContent w="100%" maxW="600px">
                <ModalHeader textStyle="h1">Modify Margin Call</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                  <Stack pb={6} spacing={8}>
                    <ModalInfoItem
                      title="Account Name / Number"
                      info={
                        <>
                          <Text>{accountHolderName}</Text>
                          <Text>{account.toUpperCase()}</Text>
                        </>
                      }
                      dataTestId="modify-call-account-name-number"
                    />
                    <ModalInfoItem title="Call Type" info={eventCode} />
                    <ModalInfoItem
                      title="Trade Date"
                      info={getDateString(tradeDate)}
                      dataTestId="modify-call-trade-date"
                    />
                    <ModalInfoItem
                      title="Issue Date"
                      info={getDateString(issueDate)}
                      dataTestId="modify-call-issue-date"
                    />
                    <InputFieldWrapper width={FIELD_WIDTH}>
                      <LabeledDatePickerInput
                        label="Due Date"
                        name="dueDate"
                        onChange={(date: string | Date) => {
                          setFieldValue(
                            "dueDate",
                            getDatePickerFormattedDate(date)
                          );
                        }}
                        onBlur={handleBlur}
                        date={values.dueDate}
                        placement="bottom"
                        isInvalid={touched.dueDate && !!errors.dueDate}
                        errorMessage={errors.dueDate}
                        isRequired
                        data-testid="due-date-input"
                      />
                    </InputFieldWrapper>
                    <InputFieldWrapper width={FIELD_WIDTH}>
                      <LabeledInput
                        label="Amount"
                        name="callAmountDisplay"
                        isRequired
                        iconLeft="$"
                        isFormattedNumber
                        value={values.callAmountDisplay}
                        onChange={({ display, raw }) => {
                          setValues({
                            ...values,
                            callAmountDisplay: display || "",
                            callAmountRaw: raw,
                          });
                        }}
                        onBlur={handleBlur}
                        isInvalid={
                          touched.callAmountDisplay &&
                          (!!errors.callAmountRaw || !!errors.callAmountDisplay)
                        }
                        errorMessage={
                          errors.callAmountRaw || errors.callAmountDisplay
                        }
                        data-testid="call-amount-input"
                      />
                    </InputFieldWrapper>
                  </Stack>
                </ModalBody>
                <ModalFooterAction>
                  <Flex>
                    <Box ml="auto">
                      <Button
                        variant="outline"
                        ref={cancelRef}
                        onClick={() => {
                          resetForm();
                          onClose();
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="primary"
                        ml={3}
                        type="submit"
                        isLoading={isLoading}
                        isDisabled={
                          !isValid || isLoading || !isFormUpdated(values)
                        }
                        data-testid="modify-call-save-button"
                      >
                        Save Changes
                      </Button>
                    </Box>
                  </Flex>
                </ModalFooterAction>
              </ModalContent>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default ModifyCallModal;
